import { Injectable } from '@angular/core';
import { TemplateType } from 'app/core/store/template/template.interface';
import { logger } from 'app/core/util/logger';
import { Observable, of } from 'rxjs';
import { appSettings } from 'app/appSettings';
import { AvailablePickingDates, OrderDetail, OrderDetailTO, RelocationDetail } from '../../store/edit-florder-detail/florder-detail.interface';
import { OrderRestService } from './order.rest.service';
import { default as cachedTemplates } from './responses/getTemplates';
import { RestService } from './rest.service';
import { map } from 'rxjs/operators'; // import { default as cachedTemplate } from './responses/getTemplate';

// import { default as cachedTemplate } from './responses/getTemplate';

@Injectable({ providedIn: 'root' })
export class TemplateRestService extends OrderRestService {
  constructor(restService: RestService) {
    super(restService);
  }

  getTemplates(type: TemplateType): Observable<any> {
    if (appSettings.USE_MOCK_REST_DATA) {
      logger.log('MOCK REST getTemplates');
      return of(cachedTemplates);
    } else {
      return this.restService.get<any[]>(`${this.getTypePath(type)}/listTemplates`).pipe(
        map(templates => {
          return [...templates.map(this.templateMaterialMapper)];
        })
      );
    }
  }

  getTemplate(type: TemplateType, templateId: number): Observable<{ data: OrderDetailTO; id: number; name: string }> {
    const url = `${this.getTypePath(type)}/getTemplate?Id=${templateId}`;
    return this.restService.get<OrderDetail>(url).pipe(map(this.templateMaterialMapper));
  }

  createOrderTemplate(type: TemplateType, florderDetail: OrderDetail): Observable<OrderDetail> {
    const postData = this.createOrderPostData(florderDetail);
    const url = `/Order/createOrUpdateTemplate`;
    return this.restService.post<OrderDetail>(url, postData);
  }

  createRelocationTemplate(type: TemplateType, florderDetail: RelocationDetail): Observable<RelocationDetail> {
    const postData = this.createRelocationPostData(florderDetail);
    const url = `/Relocation/createOrUpdateTemplate`;
    return this.restService.post<RelocationDetail>(url, postData);
  }

  deleteTemplate(type: TemplateType, templateId: number): Observable<any> {
    return this.restService.post<any>(`${this.getTypePath(type)}/deleteTemplate`, {
      id: templateId,
    });
  }

  getTemplateOrderDeliveryDates(template, startDate: string): Observable<AvailablePickingDates> {
    let url =
      `/Order/getDeliveryDates?` +
      `OrderType=${template.data.orderType}&` +
      `FromId=${template.data.fromId}&` +
      `ToId=${template.data.toId}&` +
      `Transport=${template.data.transport}&` +
      `NumberOfMonths=2`;
    if (startDate != null) url += `&StartDate=${startDate}`;
    return this.restService.get<AvailablePickingDates>(url);
  }

  getTemplateRelocationUnloadingDates(template, startDate: string): Observable<AvailablePickingDates> {
    let url = `/Relocation/getLoadingDates?` + `FromId=${template.data.fromId}&` + `ToId=${template.data.toId}&` + `NumberOfMonths=2`;
    if (startDate != null) url += `&StartDate=${startDate}`;
    return this.restService.get<AvailablePickingDates>(url);
  }

  protected createOrderPostData(orderDetail: OrderDetail): any {
    return {
      id: orderDetail.templateId,
      name: orderDetail.templateName,
      data: {
        ...super.createPostData(orderDetail),
        fullTruck: orderDetail.fullTruck,
        from: orderDetail.deliveryMethod.from,
        to: orderDetail.deliveryMethod.to,
        requestedUnloadingDates: null,
        requestedUnloadingDate: null,
      },
    };
  }

  protected createRelocationPostData(relocationDetail: RelocationDetail): any {
    return {
      id: relocationDetail.templateId,
      name: relocationDetail.templateName,
      data: {
        ...super.createPostData(relocationDetail),
        from: relocationDetail.deliveryMethod.from,
        to: relocationDetail.deliveryMethod.to,
        requestedUnloadingDates: null,
        requestedUnloadingDate: null,
      },
    };
  }

  private templateMaterialMapper(template) {
    let internalId = 0;
    return {
      ...template,
      data: {
        ...template.data,
        lineItem: template.data.lineItem.map(li => {
          return {
            ...li,
            internalId: internalId++,
            type: (li.palletId && li.packingId && 'combination') || (li.palletId && 'pallet') || 'packing',
          };
        }),
      },
    };
  }

  private getTypePath(type: TemplateType): string {
    switch (type) {
      case 'ORDER':
        return '/Order';
      case 'RELOCATION':
        return '/Relocation';
    }
    throw new Error(`Template type ${type} not implemented`);
  }
}
